import { TrackService } from './track.service';
import { YouTubeService } from './youtube.service';
import type { Track } from '@/types';
import { getFunctions, httpsCallable } from 'firebase/functions';

export class AutoMixService {
    /**
     * Generates and adds a relevant next track to the playlist
     * @param playlistId The ID of the playlist to update
     * @param currentTracks List of current tracks to avoid duplicates
     * @param seedTrack The track to base recommendations on (usually the last one)
     * @param adminId The ID of the user performing this action (the host)
     */
    static async generateNextTrack(
        playlistId: string,
        currentTracks: Track[],
        seedTrack: Track,
        adminId: string
    ): Promise<boolean> {
        try {
            console.log(`AutoMix: Generating recommendation based on "${seedTrack.title}" by ${seedTrack.artist}`);

            let suggestion = '';

            // 1. Try AI Suggestion
            try {
                const functions = getFunctions();
                const suggestFn = httpsCallable(functions, 'suggest_next_track_v2');

                const history = currentTracks.map(t => `${t.artist} - ${t.title}`);
                const currentStr = `${seedTrack.artist} - ${seedTrack.title}`;

                const result = await suggestFn({
                    history: history,
                    current_track: currentStr
                });

                const data = result.data as any;

                if (data.debug_info) {
                    console.groupCollapsed('AutoMix AI Debug Info');
                    console.log('Prompt Context:', data.debug_info);
                    console.log('Raw AI Response:', data.debug_info.raw_response);
                    console.log('Cleaned Suggestion:', data.debug_info.cleaned_text);
                    if (data.debug_info.error_body) {
                        console.error('AI API Error Body:', data.debug_info.error_body);
                    }
                    console.groupEnd();
                }

                if (data.suggestion) {
                    suggestion = data.suggestion;
                    console.log(`AutoMix: AI Suggested "${suggestion}"`);
                } else {
                    console.warn('AutoMix: AI returned no suggestion', data.error);
                }
            } catch (aiError) {
                console.error('AutoMix: AI call failed, falling back to local logic.', aiError);
            }

            // 2. Fallback to Local Logic if AI failed/empty
            let query = '';
            let isFallback = false;

            if (suggestion) {
                query = suggestion;
            } else {
                console.log('AutoMix: Using local fallback search strats.');
                // Fallback Strategy: Search for more songs by the SAME artist
                // We use "Best songs by [Artist]" to get popular hits
                query = `Best songs by ${seedTrack.artist}`;
                isFallback = true;
            }

            // 3. Fetch candidates from YouTube
            const results = await YouTubeService.searchVideos(query);

            if (!results || results.length === 0) {
                console.warn('AutoMix: No results found.');
                return false;
            }

            // 4. Filter candidates
            // Normalize seed artist for immediate repetition check
            const currentArtistNormalized = this.normalizeString(seedTrack.artist || '');

            // Pre-calculate normalized titles of ALL existing tracks to prevent repeats/variations
            // We strip the artist from the title to get the "core" song name
            const existingTitles = new Set(currentTracks.map(t => this.normalizeTitle(t.title, t.artist)));

            const validCandidate = results.find(candidate => {
                // A. Exact Duplicate Check (ID)
                if (currentTracks.some(t => t.videoId === candidate.videoId)) return false;

                // B. Immediate Artist Repetition Check
                // ONLY apply strict artist check if this is NOT a fallback.
                // If it IS a fallback, we explicitly searched for this artist, so we ALLOW it.
                if (!isFallback) {
                    const candidateArtistNormalized = this.normalizeString(candidate.channelTitle);
                    // "repeat artists are OK but not repeating the artist of the currently playing song"
                    if (candidateArtistNormalized.includes(currentArtistNormalized) || currentArtistNormalized.includes(candidateArtistNormalized)) {
                        return false;
                    }
                }

                // C. Fuzzy Title/Variation Check
                // "exclude variations of the currently playing song, or any songs that were already previously played"
                // Strip candidate artist from candidate title too
                const candidateTitleNormalized = this.normalizeTitle(candidate.title, candidate.channelTitle);

                if (existingTitles.has(candidateTitleNormalized)) {
                    console.log(`AutoMix: Skipping "${candidate.title}" (variation of existing track)`);
                    return false;
                }

                // D. Current Song Variation Check (Stronger)
                const seedTitleNormalized = this.normalizeTitle(seedTrack.title, seedTrack.artist);

                // Check for overlapping words (e.g. "Sledgehammer" in "Sledgehammer 2012")
                // Only if length is significant to avoid matching "The" or "A"
                if (seedTitleNormalized.length > 3 && candidateTitleNormalized.includes(seedTitleNormalized)) {
                    console.log(`AutoMix: Skipping "${candidate.title}" (contained in seed title)`);
                    return false;
                }
                if (candidateTitleNormalized.length > 3 && seedTitleNormalized.includes(candidateTitleNormalized)) {
                    console.log(`AutoMix: Skipping "${candidate.title}" (contains seed title)`);
                    return false;
                }

                return true;
            });

            if (!validCandidate) {
                console.warn('AutoMix: No valid candidates found after filtering.');
                return false;
            }

            console.log(`AutoMix: Queuing "${validCandidate.title}" by ${validCandidate.channelTitle}`);

            // 5. Add to playlist
            const newTrack: Omit<Track, 'addedAt' | 'lastVotedAt'> = {
                id: crypto.randomUUID(),
                videoId: validCandidate.videoId,
                title: validCandidate.title,
                artist: validCandidate.channelTitle,
                thumbnail: validCandidate.thumbnail,
                duration: validCandidate.duration || 0,
                addedBy: adminId,
                score: 0,
                votes: {}
            };

            await TrackService.addTrack(playlistId, newTrack, adminId);
            return true;

        } catch (error) {
            console.error('AutoMix: Failed to generate next track', error);
            return false;
        }
    }

    private static normalizeString(str: string): string {
        return str.toLowerCase().replace(/[^a-z0-9 ]/g, '').trim();
    }

    private static normalizeTitle(title: string, artist?: string): string {
        let normalized = title.toLowerCase();

        // If artist is provided, strip it from the title (common in YouTube titles like "Artist - Song")
        if (artist) {
            const artistLower = artist.toLowerCase();
            // Remove "Artist -" or "- Artist" patterns
            normalized = normalized.replace(artistLower, '');
        }

        // Remove content in brackets/parentheses: "Song (Remix)" -> "Song "
        normalized = normalized.replace(/\([^)]*\)/g, '').replace(/\[[^\]]*\]/g, '');

        // Remove common non-essential keywords
        const stopWords = ['official video', 'lyrics', 'feat', 'ft.', 'music video', 'hd', 'hq', 'audio', 'video', 'official', 'ai music video'];
        stopWords.forEach(word => {
            normalized = normalized.replace(word, '');
        });

        // Remove non-alphanumeric chars (keep spaces for word separation)
        normalized = normalized.replace(/[^a-z0-9 ]/g, '');

        // Collapse multiple spaces
        return normalized.replace(/\s+/g, ' ').trim();
    }
}
